import React from 'react';
import { Smartphone, Zap, ShieldCheck, Bell, ChevronRight, Apple, PlayCircle, QrCode } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export const MobileLandingView: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-800 selection:bg-blue-200">
      
      {/* Navbar (minimalist) */}
      <nav className="fixed w-full z-50 transition-all duration-300 bg-white/80 backdrop-blur-md border-b border-slate-200/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-600 to-sky-400 flex items-center justify-center text-white font-bold text-sm shadow-md">
              SD
            </div>
            <span className="font-bold text-lg text-slate-800 tracking-tight">SDMT Ponorogo</span>
          </div>
          <div className="hidden md:flex gap-8 items-center text-sm font-medium text-slate-600">
            <a href="#fitur" className="hover:text-blue-600 transition-colors">Fitur</a>
            <a href="#teknologi" className="hover:text-blue-600 transition-colors">Teknologi</a>
            <button onClick={() => navigate('/expo-simulator')} className="px-5 py-2 rounded-full bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors">
              Buka Web App
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden">
        {/* Background Decorative Elements */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full max-w-[1200px] pointer-events-none">
          <div className="absolute top-[10%] left-[-10%] w-[500px] h-[500px] rounded-full bg-sky-200/40 blur-3xl opacity-50 mix-blend-multiply animate-blob"></div>
          <div className="absolute top-[20%] right-[-10%] w-[400px] h-[400px] rounded-full bg-blue-300/40 blur-3xl opacity-50 mix-blend-multiply animate-blob animation-delay-2000"></div>
          <div className="absolute -bottom-[10%] left-[20%] w-[600px] h-[600px] rounded-full bg-indigo-200/40 blur-3xl opacity-50 mix-blend-multiply animate-blob animation-delay-4000"></div>
        </div>

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">
            
            {/* Hero Text */}
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 border border-blue-100 text-blue-700 text-sm font-medium mb-6">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500"></span>
                </span>
                Versi 2.4.0 (Expo Ready)
              </div>
              <h1 className="text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 mb-6 leading-[1.15]">
                Sistem Pembayaran <br/>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-sky-400">Dalam Genggaman</span>
              </h1>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                Aplikasi mobile eksklusif untuk wali murid SD Muhammadiyah Terpadu Ponorogo. 
                Pantau tagihan, lakukan pembayaran Instan via QRIS, dan dapatkan notifikasi secara Real-time.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <button className="flex items-center justify-center gap-3 px-8 py-4 rounded-2xl bg-slate-900 text-white font-medium hover:bg-slate-800 transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5">
                  <Apple size={22} className="mb-1" />
                  <div className="text-left">
                    <div className="text-[10px] text-slate-300 leading-none">Download di</div>
                    <div className="text-lg leading-tight">App Store</div>
                  </div>
                </button>
                <button className="flex items-center justify-center gap-3 px-8 py-4 rounded-2xl bg-white border border-slate-200 text-slate-800 font-medium hover:bg-slate-50 transition-all shadow-sm hover:shadow-md hover:-translate-y-0.5">
                  <PlayCircle size={22} className="text-blue-500" />
                  <div className="text-left">
                    <div className="text-[10px] text-slate-500 leading-none">Download di</div>
                    <div className="text-lg leading-tight">Google Play</div>
                  </div>
                </button>
              </div>

              <div className="mt-10 flex items-center gap-4 text-sm text-slate-500">
                <div className="flex -space-x-2">
                  <div className="w-8 h-8 rounded-full bg-slate-200 border-2 border-white flex items-center justify-center text-xs font-bold text-slate-500">A</div>
                  <div className="w-8 h-8 rounded-full bg-blue-200 border-2 border-white flex items-center justify-center text-xs font-bold text-blue-700">B</div>
                  <div className="w-8 h-8 rounded-full bg-sky-200 border-2 border-white flex items-center justify-center text-xs font-bold text-sky-700">C</div>
                </div>
                <p>Telah digunakan oleh <b>1,200+</b> wali murid aktif.</p>
              </div>
            </div>

            {/* Hero Mockup */}
            <div className="relative mx-auto lg:ml-auto lg:mr-0 w-full max-w-[340px]">
              {/* Glass decorative cards behind phone */}
              <div className="absolute top-20 -left-16 w-48 p-4 bg-white/80 backdrop-blur-xl border border-white/50 rounded-2xl shadow-xl z-20 animate-float hidden md:block">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                    <ShieldCheck size={16} className="text-emerald-600" />
                  </div>
                  <div className="text-sm font-bold text-slate-800">Pembayaran Sukses</div>
                </div>
                <div className="text-xs text-slate-500">SPP Bulan Agustus 2026 telah terbayar via QRIS Duitku.</div>
              </div>

              {/* Phone Frame */}
              <div className="relative z-10 w-full aspect-[1/2.1] bg-slate-900 rounded-[44px] p-2 shadow-2xl border-4 border-slate-800 transform rotate-[-2deg] hover:rotate-0 transition-transform duration-500">
                <div className="w-full h-full bg-white rounded-[36px] overflow-hidden relative">
                  {/* Notch */}
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-6 bg-slate-900 rounded-b-3xl z-20"></div>
                  
                  {/* Mockup Content (Similar to App UI) */}
                  <div className="w-full h-full bg-slate-50 flex flex-col">
                    <div className="bg-gradient-to-br from-blue-600 to-sky-500 pt-12 pb-6 px-5 rounded-b-[28px] shadow-sm">
                      <div className="flex justify-between items-center mb-6 text-white">
                        <div>
                          <div className="text-xs text-blue-100 mb-1">Selamat Datang,</div>
                          <div className="font-bold text-lg">Asfa Neysa</div>
                        </div>
                        <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                          <Bell size={18} />
                        </div>
                      </div>
                      <div className="bg-white/20 backdrop-blur-md rounded-2xl p-4 border border-white/20">
                        <div className="text-blue-50 text-xs mb-1">Tagihan Belum Dibayar</div>
                        <div className="text-2xl font-bold text-white mb-2">Rp 450.000</div>
                        <div className="bg-white text-blue-600 text-xs font-bold py-2 rounded-lg text-center mt-2 cursor-pointer">
                          Bayar Sekarang
                        </div>
                      </div>
                    </div>

                    <div className="flex-1 p-5">
                      <div className="font-bold text-slate-800 mb-4">Akses Cepat</div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-white p-4 rounded-2xl shadow-sm border border-slate-100 flex flex-col items-center gap-2">
                          <div className="w-12 h-12 bg-sky-50 rounded-full flex items-center justify-center">
                            <QrCode size={24} className="text-sky-500" />
                          </div>
                          <div className="text-xs font-semibold text-slate-700">QR Bayar</div>
                        </div>
                        <div className="bg-white p-4 rounded-2xl shadow-sm border border-slate-100 flex flex-col items-center gap-2">
                          <div className="w-12 h-12 bg-emerald-50 rounded-full flex items-center justify-center">
                            <ShieldCheck size={24} className="text-emerald-500" />
                          </div>
                          <div className="text-xs font-semibold text-slate-700">Riwayat</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
          </div>
        </div>
      </div>

      {/* Feature Highlight Section */}
      <div id="fitur" className="py-24 bg-white relative">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Teknologi Terdepan, Keamanan Ekstra</h2>
            <p className="text-slate-600">Aplikasi wali murid kami dibangun dengan teknologi terkini (React Native & Node.js) yang menjamin kecepatan, keandalan, dan kemudahan bagi semua pihak.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            
            <div className="p-8 rounded-3xl bg-slate-50 border border-slate-100 hover:shadow-lg transition-shadow group">
              <div className="w-14 h-14 bg-blue-100 text-blue-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Zap size={28} />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Real-time API</h3>
              <p className="text-slate-600 leading-relaxed">
                Sinkronisasi data langsung dengan server pusat. Tagihan muncul detik itu juga, dan status pembayaran berubah jadi "Lunas" secara seketika.
              </p>
            </div>

            <div className="p-8 rounded-3xl bg-slate-50 border border-slate-100 hover:shadow-lg transition-shadow group">
              <div className="w-14 h-14 bg-rose-100 text-rose-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <ShieldCheck size={28} />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Duitku Payment Gateway</h3>
              <p className="text-slate-600 leading-relaxed">
                Terintegrasi resmi dengan Duitku. Mendukung pembayaran instan via Virtual Account BCA/Mandiri/BRI, QRIS GPN, E-Wallet, hingga gerai retail.
              </p>
            </div>

            <div className="p-8 rounded-3xl bg-slate-50 border border-slate-100 hover:shadow-lg transition-shadow group">
              <div className="w-14 h-14 bg-amber-100 text-amber-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Bell size={28} />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Push Notifications</h3>
              <p className="text-slate-600 leading-relaxed">
                Tak perlu khawatir terlewat tagihan. Sistem cerdas (Expo Notifications) akan mengirim pesan pengingat langsung ke layar smartphone Anda.
              </p>
            </div>

          </div>
        </div>
      </div>

      {/* Try It Out CTA */}
      <div className="py-24 bg-slate-900 text-white text-center relative overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-blue-500/20 blur-[100px] rounded-full pointer-events-none"></div>
        
        <div className="max-w-3xl mx-auto px-6 relative z-10">
          <Smartphone size={48} className="mx-auto text-blue-400 mb-6" />
          <h2 className="text-4xl font-bold mb-6">Ingin Mencoba Aplikasinya?</h2>
          <p className="text-lg text-slate-300 mb-10 leading-relaxed">
            Scan QR Code di bawah ini menggunakan aplikasi <b>Expo Go</b> di Android atau Kamera bawaan di iOS untuk menjalankan simulator aplikasi secara langsung di perangkat Anda.
          </p>
          
          <div className="bg-white p-6 rounded-3xl inline-block shadow-2xl mb-8">
            {/* Dummy QR for Expo Go - Typically this points to exp://... */}
            <div className="w-48 h-48 bg-slate-100 rounded-xl flex items-center justify-center border-2 border-dashed border-slate-300">
              <QrCode size={64} className="text-slate-400" />
              <div className="absolute font-bold text-slate-800 text-sm bg-white px-2 py-1 rounded shadow-sm">
                EXPO GO
              </div>
            </div>
          </div>
          
          <div>
            <button 
              onClick={() => navigate('/expo-simulator')}
              className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 font-semibold"
            >
              Atau coba simulator via Web Browser <ChevronRight size={18} />
            </button>
          </div>
        </div>
      </div>

    </div>
  );
};
